import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PersonCustomFieldResponse } from '@tabita/contracts';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { peopleApi } from '@/services/people.api';
import { ApiError } from '@/services/api-client';
import { Alert, Button, Field, Spinner } from '@/components/ui';

/**
 * A person's custom field values — shown on the Info tab as its own card,
 * the same placement `HouseholdCard` and `RelationshipsCard` use.
 *
 * Renders nothing when the tenant has not purchased `people.custom_fields`
 * (the list call 403s) or has defined no fields yet (an empty list) — there
 * is nothing useful to show either way.
 */
export function CustomFieldsCard({ personId }: { readonly personId: string }) {
  const { t } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canRead = can('people.customField.read');

  const query = useQuery({
    queryKey: ['people', 'customFields', personId],
    queryFn: ({ signal }) => peopleApi.getPersonCustomFields(personId, signal),
    enabled: canRead,
  });

  const save = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      peopleApi.setPersonCustomFields(personId, values),
    onSuccess: (fields) => {
      queryClient.setQueryData(['people', 'customFields', personId], fields);
      setDraft(null);
      setError(null);
    },
    onError: (err: unknown) => setError(describeError(err, t)),
  });

  if (!canRead) return null;

  const fields = query.data ?? [];
  if (query.isSuccess && fields.length === 0) return null;

  const valueFor = (field: PersonCustomFieldResponse) =>
    draft?.[field.key] !== undefined ? draft[field.key] : (field.value ?? '');

  const setValue = (key: string, value: unknown) => {
    const base: Record<string, unknown> = { ...(draft ?? {}) };
    if (draft === null) for (const field of fields) base[field.key] = field.value ?? '';
    base[key] = value;
    setDraft(base);
  };

  return (
    <section className="detail-section">
      <h2 className="detail-section-title">{t('people.customFields.title')}</h2>

      {error && <Alert tone="danger">{error}</Alert>}
      {query.isPending && <Spinner label={t('common.states.loading')} />}

      {fields.length > 0 && (
        <form
          className="form-grid"
          onSubmit={(event) => {
            event.preventDefault();
            if (draft) save.mutate(draft);
          }}
        >
          {fields.map((field) => (
            <CustomFieldInput
              key={field.id}
              field={field}
              value={valueFor(field)}
              onChange={(value) => setValue(field.key, value)}
              disabled={!can('people.customField.update')}
            />
          ))}

          {draft && can('people.customField.update') && (
            <Button type="submit" variant="primary" loading={save.isPending}>
              {t('common.actions.save')}
            </Button>
          )}
        </form>
      )}
    </section>
  );
}

function CustomFieldInput({
  field,
  value,
  onChange,
  disabled,
}: {
  readonly field: PersonCustomFieldResponse;
  readonly value: unknown;
  readonly onChange: (value: unknown) => void;
  readonly disabled: boolean;
}) {
  const label = `${field.label}${field.required ? ' *' : ''}`;

  if (field.type === 'CHECKBOX') {
    return (
      <label className="checkbox">
        <input
          type="checkbox"
          checked={Boolean(value)}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
        />
        {label}
      </label>
    );
  }

  if (field.type === 'SELECT') {
    return (
      <div className="field">
        <label className="field-label" htmlFor={`custom-field-${field.id}`}>
          {label}
        </label>
        <select
          id={`custom-field-${field.id}`}
          className="field-input"
          value={typeof value === 'string' ? value : ''}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        >
          <option value="" />
          {(field.options ?? []).map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (field.type === 'TEXTAREA') {
    return (
      <div className="field">
        <label className="field-label" htmlFor={`custom-field-${field.id}`}>
          {label}
        </label>
        <textarea
          id={`custom-field-${field.id}`}
          className="field-input"
          value={typeof value === 'string' ? value : ''}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
      </div>
    );
  }

  return (
    <Field
      label={label}
      name={`custom-field-${field.id}`}
      type={field.type === 'NUMBER' ? 'number' : field.type === 'DATE' ? 'date' : 'text'}
      value={typeof value === 'number' ? String(value) : (value as string)}
      disabled={disabled}
      onChange={(event) =>
        onChange(field.type === 'NUMBER' ? Number(event.target.value) : event.target.value)
      }
    />
  );
}

function describeError(error: unknown, t: ReturnType<typeof useI18n>['t']): string {
  return error instanceof ApiError
    ? t(`errors.${error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
    : t('errors.INTERNAL_ERROR');
}
